import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Observable } from 'rxjs'
import { IAuditTrailResult } from '../models/audit/audit-trail-result.interface'
import { IAuditOptions } from '../models/audit/audit-options.interface'
import { KeyingAppEnvironmentConfig } from '../configuration/keying.app.environment.config'

@Injectable({
  providedIn: 'root',
})
export class AuditService {

  constructor(
    private http: HttpClient,
    private keyingAppEnvironmentConfig: KeyingAppEnvironmentConfig) {}

  /**
   * Gets audit trail for all entities scoped to a structure
   */
  getDefaultAuditTrail(structureId: number, options?: IAuditOptions): Observable<IAuditTrailResult> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/ActivityLog/Structures/${structureId}`
    const params = this.buildHttpParams(options)
    return this.http.get<IAuditTrailResult>(url, { params })
  }

  /**
   * Gets audit trail for Feature entities (independent of structure/key system)
   */
  getFeatureAuditTrail(options?: IAuditOptions): Observable<IAuditTrailResult> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/ActivityLog/features`
    const params = this.buildHttpParams(options)
    return this.http.get<IAuditTrailResult>(url, { params })
  }

  /**
   * Private method to build HTTP parameters from options
   */
  private buildHttpParams(options?: IAuditOptions): HttpParams {
    let params = new HttpParams()

    // Always include details for audit trails
    params = params.set('includeDetails', 'true')

    if (!options) return params

    // Map frontend options to backend parameter names
    const paramMappings = [
      // Pagination
      { key: 'pageSize', value: options.pageSize?.toString() },
      { key: 'pageIndex', value: options.pageIndex?.toString() },

      // Content control
      { key: 'includeDetails', value: options.includeDetails?.toString() },
      { key: 'includeSecuredFields', value: options.includeSecuredFields?.toString() },

      // Date filtering (support both old and new names)
      { key: 'fromDate', value: options.fromDate || options.dateFrom },
      { key: 'toDate', value: options.toDate || options.dateTo },

      // Entity filtering
      { key: 'structureId', value: options.structureId?.toString() },
      { key: 'keySystemId', value: options.keySystemId?.toString() },
      { key: 'userId', value: options.userId?.toString() },

      // Search (support both old and new names)
      { key: 'searchTerm', value: options.searchTerm || options.searchText },

      // Sorting
      { key: 'sortBy', value: options.sortBy },
      { key: 'sortAscending', value: options.sortAscending?.toString() },
    ]

    paramMappings.forEach(({ key, value }) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value)
      }
    })

    // Handle array parameters
    if (options.actions?.length) {
      options.actions.forEach((action: string) => {
        params = params.append('actions', action)
      })
    }

    if (options.entityTypes?.length) {
      options.entityTypes.forEach((entityType: string) => {
        params = params.append('entityTypes', entityType)
      })
    }

    if (options.sources?.length) {
      options.sources.forEach((source: string) => {
        params = params.append('sources', source)
      })
    }

    return params
  }
}
